import type { OPCRowInformation } from '../../../../config/flavourConfig';
import type {
    CoverableChangesDTO,
    CoverableDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { getCoverableDiffsDTO, isChangeTypeAvailable } from '../../../../utils/opcChangesUtil';
import type {
    PrhHolidayHomeDTO,
    PrhHouseDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';

const NUM_OF_TOILETS = 'ToiletsNumber';
const PRIMARY_HEATING = 'PrimaryHeating';
const FIRE_PLACE = 'FirePlace';
const HOUSE_FLAVOR = 'top_prh_house';
const HOLIDAY_HOME_FLAVOR = 'top_prh_holiday_home';
interface IPrhOpcAttribute {
    isNumOfToiletsAvailableForUpdate: boolean;
    isPrimaryHeatingAvailableForUpdate: boolean;
    isFirePlaceAvailableForUpdate: boolean;
}

const getHouseAdjustDetailsConfig = (
    opcAttributes: IPrhOpcAttribute,
    fixedId: string,
    value: PrhHouseDTO,
    isOpc: boolean
): OPCRowInformation[] => {
    const { isNumOfToiletsAvailableForUpdate, isPrimaryHeatingAvailableForUpdate } = opcAttributes;
    return [
        {
            name: 'Antal toiletter',
            fieldValue: value.numberOfToilets?.toString(),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_prh_house',
                coverableId: fixedId,
                attribute: 'prhNumOfToilets'
            },
            hidden: isOpc && !isNumOfToiletsAvailableForUpdate
        },
        {
            name: 'Opvarmning',
            fieldValue: value.primaryHeatingType,
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_prh_house',
                coverableId: fixedId,
                attribute: 'prhPrimaryHeating'
            },
            hidden: isOpc && !isPrimaryHeatingAvailableForUpdate
        }
    ];
};

const getHolidayHomeAdjustDetailsConfig = (
    opcAttributes: IPrhOpcAttribute,
    fixedId: string,
    prhHouse: PrhHolidayHomeDTO,
    isOpc: boolean
): OPCRowInformation[] => {
    const {
        isNumOfToiletsAvailableForUpdate,
        isPrimaryHeatingAvailableForUpdate,
        isFirePlaceAvailableForUpdate
    } = opcAttributes;
    return [
        {
            name: 'Antal toiletter',
            fieldValue: prhHouse.numberOfToilets?.toString(),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_prh_holiday_home',
                coverableId: fixedId,
                attribute: 'prhNumOfToilets'
            },
            hidden: isOpc && !isNumOfToiletsAvailableForUpdate
        },
        {
            name: 'Opvarmning',
            fieldValue: prhHouse.primaryHeatingType,
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_prh_holiday_home',
                coverableId: fixedId,
                attribute: 'prhPrimaryHeating'
            },
            hidden: isOpc && !isPrimaryHeatingAvailableForUpdate
        },
        {
            name: 'Brændeovn/pejs',
            fieldValue: prhHouse.fireplace,
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_prh_holiday_home',
                coverableId: fixedId,
                attribute: 'prhFirePlace'
            },
            hidden: isOpc && !isFirePlaceAvailableForUpdate
        }
    ];
};

const getPrhAdjustDetailsConfig = (
    prhHouse: CoverableDTO,
    isOpc: boolean,
    coverableChangesDTO: CoverableChangesDTO[]
): OPCRowInformation[] => {
    const fixedId = prhHouse.fixedId ?? '';
    const changes = getCoverableDiffsDTO(coverableChangesDTO, 'PRH', fixedId);

    const isNumOfToiletsAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: NUM_OF_TOILETS
        },
        changes
    );
    const isPrimaryHeatingAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: PRIMARY_HEATING
        },
        changes
    );
    const isFirePlaceAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: FIRE_PLACE
        },
        changes
    );
    const opcAttributes = {
        isNumOfToiletsAvailableForUpdate,
        isPrimaryHeatingAvailableForUpdate,
        isFirePlaceAvailableForUpdate
    };

    switch (prhHouse.flavourCode) {
        case HOUSE_FLAVOR:
            return getHouseAdjustDetailsConfig(
                opcAttributes,
                fixedId,
                prhHouse as PrhHouseDTO,
                isOpc
            );
        case HOLIDAY_HOME_FLAVOR:
            return getHolidayHomeAdjustDetailsConfig(
                opcAttributes,
                fixedId,
                prhHouse as PrhHolidayHomeDTO,
                isOpc
            );
        default:
            return [];
    }
};

export default getPrhAdjustDetailsConfig;
